/* eslint-disable prefer-arrow-callback */
import React from 'react';
import { filter, find } from 'lodash';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardActions, CardContent, Grid, Paper, Typography } from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';

import { Footer, MessageDisplay, NonProdBanner, SecurityQuestionFieldGroup } from '@/components';
import { useAppContext } from '@/context';
import { avLogoWhite, avBackgroundOrange } from '@/static';
import { useFRCallback } from '@/hooks';

const currentEnv = getSpecificEnv();
const shouldShowNonProdBanner = currentEnv !== 'prod';

// for use when user clicks 'back' button; FR requires some values to be present.
const dummyFormValues = {
  IDToken1question: 'Who was your first employer?',
  IDToken1answer: 'x',
  IDToken2question: 'In what city were you born?',
  IDToken2answer: 'x',
  IDToken3question: 'In what city did you meet your spouse or significant other?',
  IDToken3answer: 'x',
};

const NewUserSecurityQuestions = ({ ...props }) => {
  const { stepToRender, setCurrentStep, formValues: registrationValues = {}, setFormValues } = props;
  const { setMessages, securityQuestionsOptions } = useAppContext();
  const { callbacks = [], choiceCallbackIndex, choiceCallbackChoices } = useFRCallback(stepToRender);
  const navigate = useNavigate();

  const { securityQuestions: securityQuestionsValues } = registrationValues;

  const securityQuestionCallbacks = filter(
    callbacks,
    (callback) => callback.type?.toLowerCase() === 'kbacreatecallback'
  );

  // initialize form
  const formHandlers = useForm({
    defaultValues: {
      IDToken1question: securityQuestionsValues.IDToken1question || '',
      IDToken1answer: securityQuestionsValues.IDToken1answer || '',
      IDToken2question: securityQuestionsValues.IDToken2question || '',
      IDToken2answer: securityQuestionsValues.IDToken2answer || '',
      IDToken3question: securityQuestionsValues.IDToken3question || '',
      IDToken3answer: securityQuestionsValues.IDToken3answer || '',
    },
  });
  const { handleSubmit } = formHandlers;

  const onSubmit = (values) => {
    setMessages([]);

    const { IDToken1answer, IDToken2answer, IDToken3answer, navigation } = values;

    // save form values to journey state if the function is passed in AND dummy values not found;
    // NOTE: for some reason, dummy values need to be passed in for the 'back' button to work with FR. in this case, shouldn't save these form values to state.
    if (setFormValues && IDToken1answer !== 'x' && IDToken2answer !== 'x' && IDToken3answer !== 'x') {
      setFormValues({ ...registrationValues, securityQuestions: { ...values } });
    }

    const output = stepToRender;

    const { payload = {} } = output;
    const { callbacks = [] } = payload;

    const newCallbacks = callbacks;

    if (values) {
      // isolate kba callbacks for iteration
      const kbaCallbacks = filter(callbacks, (callback) => callback?.type === 'KbaCreateCallback');

      // iterate through original callbacks
      for (const [index, callbackObj] of kbaCallbacks.entries()) {
        // find the question and answer objects in the input for each callback
        const { input = [] } = callbackObj;
        const questionObj = find(input, (inputObj) => inputObj?.name?.indexOf('question') >= 0);
        const answerObj = find(input, (inputObj) => inputObj?.name?.indexOf('answer') >= 0);

        // get the field names
        const questionFieldId = questionObj?.name;
        const answerFieldId = answerObj?.name;

        // assign the field values from the form
        questionObj.value = values[questionFieldId];
        answerObj.value = values[answerFieldId]?.trim();

        // build the new input array with the form values
        const newInput = [{ ...questionObj }, { ...answerObj }];

        // override the original input with the new one
        newCallbacks[index].input = newInput;
      }

      if (navigation === 'back') {
        // handle back navigation if values not passed in
        const backNavigationIndex = choiceCallbackChoices.indexOf('back') || 0; // default to 0 if not found for whatever reason (i.e. spelling in the journey)

        // override the original input with the new one
        newCallbacks[choiceCallbackIndex].input[0].value = backNavigationIndex;

        // override the old callbacks with the new ones.
        Object.assign(output.payload.callbacks, [...newCallbacks]);
      } else {
        // override the old callbacks with the new ones.
        Object.assign(output.payload.callbacks, [...newCallbacks]);
      }
    }

    setCurrentStep({ ...output });
  };

  return securityQuestionsOptions ? (
    <Grid
      id="new-user-security-questions-page-grid-container"
      container
      display="flex"
      flexDirection="column"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      {shouldShowNonProdBanner ? (
        <Grid
          id="nonprod-banner-grid-container"
          display="flex"
          mx={2}
          mt={2}
          justifyContent="center"
          sx={{ marginBottom: { xs: 2, md: 0 } }}
        >
          <NonProdBanner />
        </Grid>
      ) : (
        <Box />
      )}
      <Grid
        id="security-questions-page-content-grid"
        container
        display="flex"
        flexDirection="row"
        justifyContent="center"
        flexWrap="wrap"
        mt="auto"
      >
        <Paper
          variant="elevation"
          elevation={6}
          sx={{
            width: { xs: '350px', sm: '500px', md: '600px' },
            marginRight: { sm: 2 },
            marginBottom: { xs: 2, md: 0 },
          }}
        >
          <FormProvider {...formHandlers}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent>
                <Typography variant="h1" mb={2}>
                  Security Questions
                </Typography>
                <Typography variant="subtitle1" mb={2}>
                  Choose three unique security questions and answers.
                </Typography>
                {securityQuestionCallbacks.map((callback) => {
                  // map out callbacks to create each security question group
                  const { input = [] } = callback;

                  const questionObj = find(input, (inputObj) => inputObj?.name?.indexOf('question') >= 0);
                  const answerObj = find(input, (inputObj) => inputObj?.name?.indexOf('answer') >= 0);

                  return (
                    <Box key={`securityQuestions-${questionObj.name}`}>
                      <SecurityQuestionFieldGroup
                        questionId={questionObj.name}
                        answerId={answerObj.name}
                        registrationValues={registrationValues}
                      />
                    </Box>
                  );
                })}
                <MessageDisplay />
              </CardContent>
              <CardActions mt="auto" sx={{ justifyContent: 'end' }}>
                <Button color="secondary" onClick={() => onSubmit({ ...dummyFormValues, navigation: 'back' })}>
                  Back
                </Button>
                <Button color="primary" type="submit">
                  Continue
                </Button>
              </CardActions>
            </form>
          </FormProvider>
        </Paper>
        <Card
          variant="elevation"
          elevation={6}
          sx={{
            alignItems: 'stretch',
            backgroundImage: `url(${avBackgroundOrange})`,
            backgroundSize: 'cover',
            backgroundClip: 'border-box',
          }}
        >
          <Grid display="flex" flexDirection="column" p={3} justifyContent="space-between" sx={{ height: '100%' }}>
            <Grid display="flex" mb="auto" justifyContent="end">
              <img
                src={avLogoWhite}
                alt="Availity Logo"
                style={{
                  maxWidth: '60%',
                }}
              />
            </Grid>
            <Grid display="flex" mb={2} justifyContent="center">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                  Already have an account?
                </Box>
              </Typography>
            </Grid>
            <Grid mb="auto">
              <Button
                color="secondary"
                onClick={() => {
                  setMessages([]); // clear out messages on route navigation
                  navigate('/login');
                }}
                sx={{ width: '100%' }}
              >
                Log in.
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Footer />
    </Grid>
  ) : null;
};

NewUserSecurityQuestions.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  formValues: PropTypes.object,
  setFormValues: PropTypes.func,
};

export default NewUserSecurityQuestions;
